import { QuestionType, Question } from '../types'
import {
  QuestionTypeConfig,
  QuestionRendererProps,
} from '../../practice/components/renderers/types'
import { getValidator } from './validatorRegistry'
import React from 'react'

// ============================================================
// Lazy Renderer Imports — Code-split per question type
// Each renderer is loaded on first use, reducing initial bundle by ~29 chunks.
// IMPORTANT: import() paths MUST be static string literals for bundler analysis.
// ============================================================

const MultipleChoiceRenderer = React.lazy(() =>
  import('../../practice/components/renderers/MultipleChoiceRenderer').then(
    (m) => ({ default: m.MultipleChoiceRenderer }),
  ),
)
const ColumnArithmeticRenderer = React.lazy(() =>
  import('../../practice/components/renderers/ColumnArithmeticRenderer').then(
    (m) => ({ default: m.ColumnArithmeticRenderer }),
  ),
)
const LongDivisionRenderer = React.lazy(() =>
  import('../../practice/components/renderers/LongDivisionRenderer').then(
    (m) => ({ default: m.LongDivisionRenderer }),
  ),
)
const MoneyAssemblerRenderer = React.lazy(() =>
  import('../../practice/components/renderers/MoneyAssemblerRenderer').then(
    (m) => ({ default: m.MoneyAssemblerRenderer }),
  ),
)
const ClockRenderer = React.lazy(() =>
  import('../../practice/components/renderers/ClockRenderer').then((m) => ({
    default: m.ClockRenderer,
  })),
)
const FillInRenderer = React.lazy(() =>
  import('../../practice/components/renderers/FillInRenderer').then((m) => ({
    default: m.FillInRenderer,
  })),
)
const OrderingRenderer = React.lazy(() =>
  import('../../practice/components/renderers/OrderingRenderer').then((m) => ({
    default: m.OrderingRenderer,
  })),
)
const MatchingRenderer = React.lazy(() =>
  import('../../practice/components/renderers/MatchingRenderer').then((m) => ({
    default: m.MatchingRenderer,
  })),
)
const DragDropFillRenderer = React.lazy(() =>
  import('../../practice/components/renderers/DragDropFillRenderer').then(
    (m) => ({ default: m.DragDropFillRenderer }),
  ),
)
const TableReadingRenderer = React.lazy(() =>
  import('../../practice/components/renderers/TableReadingRenderer').then(
    (m) => ({ default: m.TableReadingRenderer }),
  ),
)
const NumberSequenceRenderer = React.lazy(() =>
  import('../../practice/components/renderers/NumberSequenceRenderer').then(
    (m) => ({ default: m.NumberSequenceRenderer }),
  ),
)
const RatioTableRenderer = React.lazy(() =>
  import('../../practice/components/renderers/RatioTableRenderer').then(
    (m) => ({ default: m.RatioTableRenderer }),
  ),
)
const TextHighlightRenderer = React.lazy(() =>
  import('../../practice/components/renderers/TextHighlightRenderer').then(
    (m) => ({ default: m.TextHighlightRenderer }),
  ),
)
const BarChartRenderer = React.lazy(() =>
  import('../../practice/components/renderers/BarChartRenderer').then((m) => ({
    default: m.BarChartRenderer,
  })),
)
const LineChartRenderer = React.lazy(() =>
  import('../../practice/components/renderers/LineChartRenderer').then((m) => ({
    default: m.LineChartRenderer,
  })),
)
const ScheduleRenderer = React.lazy(() =>
  import('../../practice/components/renderers/ScheduleRenderer').then((m) => ({
    default: m.ScheduleRenderer,
  })),
)
const PieChartRenderer = React.lazy(() =>
  import('../../practice/components/renderers/PieChartRenderer').then((m) => ({
    default: m.PieChartRenderer,
  })),
)
const MultiplicationGridRenderer = React.lazy(() =>
  import('../../practice/components/renderers/MultiplicationGridRenderer').then(
    (m) => ({ default: m.MultiplicationGridRenderer }),
  ),
)
const TrueFalseSeriesRenderer = React.lazy(() =>
  import('../../practice/components/renderers/TrueFalseSeriesRenderer').then(
    (m) => ({ default: m.TrueFalseSeriesRenderer }),
  ),
)
const EstimationSliderRenderer = React.lazy(() =>
  import('../../practice/components/renderers/EstimationSliderRenderer').then(
    (m) => ({ default: m.EstimationSliderRenderer }),
  ),
)
const CategorizeRenderer = React.lazy(() =>
  import('../../practice/components/renderers/CategorizeRenderer').then(
    (m) => ({ default: m.CategorizeRenderer }),
  ),
)
const ErrorSpottingRenderer = React.lazy(() =>
  import('../../practice/components/renderers/ErrorSpottingRenderer').then(
    (m) => ({ default: m.ErrorSpottingRenderer }),
  ),
)
const GridShadeRenderer = React.lazy(() =>
  import('../../practice/components/renderers/GridShadeRenderer').then((m) => ({
    default: m.GridShadeRenderer,
  })),
)
const StepSolverRenderer = React.lazy(() =>
  import('../../practice/components/renderers/StepSolverRenderer').then(
    (m) => ({ default: m.StepSolverRenderer }),
  ),
)
const SentenceParserRenderer = React.lazy(() =>
  import('../../practice/components/renderers/SentenceParserRenderer').then(
    (m) => ({ default: m.SentenceParserRenderer }),
  ),
)
const ComparisonJudgeRenderer = React.lazy(() =>
  import('../../practice/components/renderers/ComparisonJudgeRenderer').then(
    (m) => ({ default: m.ComparisonJudgeRenderer }),
  ),
)
const VerbConjugationFlowRenderer = React.lazy(() =>
  import(
    '../../practice/components/renderers/VerbConjugationFlowRenderer'
  ).then((m) => ({ default: m.VerbConjugationFlowRenderer })),
)
const ThermometerSliderRenderer = React.lazy(() =>
  import('../../practice/components/renderers/ThermometerSliderRenderer').then(
    (m) => ({ default: m.ThermometerSliderRenderer }),
  ),
)
const SentenceErrorJudgeRenderer = React.lazy(() =>
  import('../../practice/components/renderers/SentenceErrorJudgeRenderer').then(
    (m) => ({ default: m.SentenceErrorJudgeRenderer }),
  ),
)

/**
 * Question Type Registry v3.1
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/config/questionTypeRegistry.ts in R25.
 *
 * v3.1 (mei 2026): All 29 renderers converted to React.lazy() dynamic imports
 * for code splitting. Each renderer is loaded on first use, reducing the initial
 * bundle by ~29 chunks (~10-30KB each). React.memo() wrapping removed from
 * registry — lazy components are cached after first load.
 *
 * Modern architecture with clean separation of concerns:
 * - questionTypeRegistry.ts → renderer mapping + metadata
 * - validatorRegistry.ts → all validation logic
 * - answerProtocol.ts → answer payload types + bridge functions
 */

/** Metadata-only config for a question type (no validation logic) */
interface QuestionTypeMeta {
  type: QuestionType
  label: string
  isInteractive: boolean
  hasBuiltInVisuals: boolean
  shouldShuffleOptions: boolean
  isCitoEligible: boolean
  validateAnswer: (userAnswer: string | number, question: Question) => boolean
  Renderer: React.ComponentType<QuestionRendererProps>
}
const QUESTION_TYPE_META: Record<QuestionType, QuestionTypeMeta> = {
  multiple_choice: {
    type: 'multiple_choice',
    label: 'Meerkeuze',
    isInteractive: false,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: true,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('multiple_choice', userAnswer, question),
    Renderer: MultipleChoiceRenderer,
  },
  cijferend_rekenen: {
    type: 'cijferend_rekenen',
    label: 'Cijferend Rekenen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('cijferend_rekenen', userAnswer, question),
    Renderer: ColumnArithmeticRenderer,
  },
  cijferend_delen: {
    type: 'cijferend_delen',
    label: 'Cijferend Delen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('cijferend_delen', userAnswer, question),
    Renderer: LongDivisionRenderer,
  },
  clock: {
    type: 'clock',
    label: 'Klokkijken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('clock', userAnswer, question),
    Renderer: ClockRenderer,
  },
  money: {
    type: 'money',
    label: 'Geld',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('money', userAnswer, question),
    Renderer: MoneyAssemblerRenderer,
  },
  fill_in: {
    type: 'fill_in',
    label: 'Invulvraag',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('fill_in', userAnswer, question),
    Renderer: FillInRenderer,
  },
  ordering: {
    type: 'ordering',
    label: 'Volgorde',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('ordering', userAnswer, question),
    Renderer: OrderingRenderer,
  },
  matching: {
    type: 'matching',
    label: 'Koppelen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('matching', userAnswer, question),
    Renderer: MatchingRenderer,
  },
  drag_drop_fill: {
    type: 'drag_drop_fill',
    label: 'Sleep en Plaats',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('drag_drop_fill', userAnswer, question),
    Renderer: DragDropFillRenderer,
  },
  table_reading: {
    type: 'table_reading',
    label: 'Tabel Aflezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('table_reading', userAnswer, question),
    Renderer: TableReadingRenderer,
  },
  number_sequence: {
    type: 'number_sequence',
    label: 'Getallenreeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('number_sequence', userAnswer, question),
    Renderer: NumberSequenceRenderer,
  },
  ratio_table: {
    type: 'ratio_table',
    label: 'Verhoudingstabel',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('ratio_table', userAnswer, question),
    Renderer: RatioTableRenderer,
  },
  text_highlight: {
    type: 'text_highlight',
    label: 'Tekst Markeren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('text_highlight', userAnswer, question),
    Renderer: TextHighlightRenderer,
  },
  bar_chart: {
    type: 'bar_chart',
    label: 'Staafgrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('bar_chart', userAnswer, question),
    Renderer: BarChartRenderer,
  },
  line_chart: {
    type: 'line_chart',
    label: 'Lijngrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('line_chart', userAnswer, question),
    Renderer: LineChartRenderer,
  },
  schedule_reading: {
    type: 'schedule_reading',
    label: 'Rooster Lezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('schedule_reading', userAnswer, question),
    Renderer: ScheduleRenderer,
  },
  pie_chart: {
    type: 'pie_chart',
    label: 'Cirkeldiagram',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('pie_chart', userAnswer, question),
    Renderer: PieChartRenderer,
  },
  multiplication_grid: {
    type: 'multiplication_grid',
    label: 'Tafelrooster',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('multiplication_grid', userAnswer, question),
    Renderer: MultiplicationGridRenderer,
  },
  true_false_series: {
    type: 'true_false_series',
    label: 'Waar/Onwaar Reeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('true_false_series', userAnswer, question),
    Renderer: TrueFalseSeriesRenderer,
  },
  estimation_slider: {
    type: 'estimation_slider',
    label: 'Schatting',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('estimation_slider', userAnswer, question),
    Renderer: EstimationSliderRenderer,
  },
  categorize: {
    type: 'categorize',
    label: 'Sorteren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('categorize', userAnswer, question),
    Renderer: CategorizeRenderer,
  },
  error_spotting: {
    type: 'error_spotting',
    label: 'Fouten Zoeken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('error_spotting', userAnswer, question),
    Renderer: ErrorSpottingRenderer,
  },

  grid_shade: {
    type: 'grid_shade',
    label: 'Raster Arceren',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('grid_shade', userAnswer, question),
    Renderer: GridShadeRenderer,
  },

  step_solver: {
    type: 'step_solver',
    label: 'Stapsgewijs Oplossen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('step_solver', userAnswer, question),
    Renderer: StepSolverRenderer,
  },

  sentence_parser: {
    type: 'sentence_parser',
    label: 'Zinsontleding',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('sentence_parser', userAnswer, question),
    Renderer: SentenceParserRenderer,
  },

  comparison_judge: {
    type: 'comparison_judge',
    label: 'Vergelijken',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('comparison_judge', userAnswer, question),
    Renderer: ComparisonJudgeRenderer,
  },

  verb_conjugation_flow: {
    type: 'verb_conjugation_flow',
    label: 'Werkwoord Vervoegen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('verb_conjugation_flow', userAnswer, question),
    Renderer: VerbConjugationFlowRenderer,
  },

  thermometer_slider: {
    type: 'thermometer_slider',
    label: 'Thermometer Slider',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('thermometer_slider', userAnswer, question),
    Renderer: ThermometerSliderRenderer,
  },

  sentence_error_judge: {
    type: 'sentence_error_judge',
    label: 'Zinsfout Herkennen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('sentence_error_judge', userAnswer, question),
    Renderer: SentenceErrorJudgeRenderer,
  },
}

/**
 * Build the QUESTION_TYPE_REGISTRY by combining
 * metadata from QUESTION_TYPE_META with validators from ValidatorRegistry.
 */
export const QUESTION_TYPE_REGISTRY: Record<QuestionType, QuestionTypeConfig> =
  Object.fromEntries(
    Object.entries(QUESTION_TYPE_META).map(([key, meta]) => [
      key,
      {
        ...meta,
        validateAnswer: (userAnswer: string | number, question: Question) =>
          getValidator(key)(userAnswer, question),
      },
    ]),
  ) as Record<QuestionType, QuestionTypeConfig>
export function getQuestionTypeConfig(type: string): QuestionTypeConfig {
  if (type === 'geld_samenstellen' || type === 'klok_aflezen') {
    throw new Error(
      `Obsolete questionType "${type}" detected. Migrate to canonical "money" or "clock".`,
    )
  }
  const config = QUESTION_TYPE_REGISTRY[type as QuestionType]
  if (!config) {
    throw new Error(
      `Unsupported questionType "${type}" in questionTypeRegistry.`,
    )
  }
  return config
}
